import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { gsap } from "gsap";
import GUI from "lil-gui";
import ThreeMeshUI from "three-mesh-ui";

// const gui = new GUI()
//

/**
 * Loaders
 */
const loadingOverlay = document.querySelector(".loading-overlay");
const loadingMessage = document.querySelector(".loading-message");

let sceneReady = false;
const apiUrl = import.meta.env.VITE_API_URL;

const loadingManager = new THREE.LoadingManager(
  // Loaded callback
  () => {
    window.setTimeout(() => {
      loadingOverlay.classList.add("ended");

      setTimeout(() => {
        loadingOverlay.style.display = "none";
      }, 500);
    }, 500);

    window.setTimeout(() => {
      sceneReady = true;
    }, 2000);
  },

  (itemUrl, itemsLoaded, itemsTotal) => {
    const progressRatio = itemsLoaded / itemsTotal;

    if (loadingMessage) {
      loadingMessage.textContent = `Loading... ${Math.round(
        progressRatio * 100
      )}%`;
    }
  }
);

// Texture loader
const textureLoader = new THREE.TextureLoader(loadingManager);
// Draco loader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("draco/");

const gltfLoader = new GLTFLoader(loadingManager);
gltfLoader.setDRACOLoader(dracoLoader);
const cubeTextureLoader = new THREE.CubeTextureLoader(loadingManager);

/**
 * Textures
 */
const bakedTexture = textureLoader.load("RouletteTable.jpg");
bakedTexture.flipY = false;
bakedTexture.colorSpace = THREE.SRGBColorSpace;

let coinModel = null;
const coinTextures = {};
coinTextures[5] = textureLoader.load("B_Coin-Grey.jpg"); // Replace with actual path
coinTextures[10] = textureLoader.load("B_Coin-Red.jpg"); // Replace with actual path
coinTextures[15] = textureLoader.load("B_Coin-SkyBlue.jpg"); // Replace with actual path
coinTextures[25] = textureLoader.load("B_Coin-Violet.jpg"); // Replace with actual path
coinTextures[30] = textureLoader.load("B_Coin-Yellow.jpg"); // Replace with actual path
let cursorCoin;
Object.values(coinTextures).forEach((texture) => {
  texture.flipY = false;
  texture.colorSpace = THREE.SRGBColorSpace;
});

gltfLoader.load(
  "Coin.glb",
  (gltf) => {
    coinModel = gltf.scene;
    coinModel.traverse((child) => {
      if (child.isMesh) {
        child.material = new THREE.MeshBasicMaterial();
      }
    });

    // Create cursor coin
    cursorCoin = coinModel.clone();
    cursorCoin.scale.set(1, 1, 1);
    cursorCoin.visible = false;
    cursorCoin.traverse((child) => {
      if (child.isMesh) {
        child.raycast = () => {};
      }
    });

    // Add text block for chip value
    const chipValueTextBlock = new ThreeMeshUI.Block({
      width: 0.08,
      height: 0.05,
      justifyContent: "center",
      alignItems: "center",
      backgroundOpacity: 0,
      fontFamily:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
      fontTexture:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
    });

    const chipValueText = new ThreeMeshUI.Text({
      content: "",
      fontSize: 0.045,
      fontColor: new THREE.Color(0x000000),
    });

    chipValueTextBlock.add(chipValueText);
    cursorCoin.add(chipValueTextBlock);
    chipValueTextBlock.position.set(0, 0.005, 0);
    chipValueTextBlock.rotation.x = -Math.PI * 0.5;
    chipValueTextBlock.rotation.z = Math.PI * 0.5;
    // Store reference to the text object
    cursorCoin.userData.chipValueText = chipValueText;

    scene.add(cursorCoin);

    // Setup Chip Selection UI
    chipValues.forEach((value, index) => {
      const button = coinModel.clone();
      button.traverse((child) => {
        if (child.isMesh) {
          const texture = coinTextures[value] || coinTextures[5];
          child.material = new THREE.MeshBasicMaterial({ map: texture });
        }
      });

      // Set initial properties
      button.scale.set(0.8, 0.8, 0.8);
      button.rotation.x = -Math.PI * 2;
      button.rotation.z = -Math.PI / 0.5;
      button.position.x = 0.9;
      button.position.y = 0.31;
      button.position.z = -0.5 + index * 0.15;

      // Store original states in userData
      button.userData = {
        value: value,
        originalScale: button.scale.clone(),
        originalRotation: button.rotation.clone(),
        originalOpacity: 1, // Default opacity
      };
      button.castShadow = true;

      uiContainer.add(button);
      chipButtons.push(button);

      // Add text label and store it with original states
      const textBlock = new ThreeMeshUI.Block({
        width: 0.1,
        height: 0.05,
        justifyContent: "center",
        alignItems: "center",
        backgroundOpacity: 0,
        fontFamily:
          "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
        fontTexture:
          "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
      });

      const text = new ThreeMeshUI.Text({
        content: `${value}`,
        fontSize: 0.03,
        fontColor: new THREE.Color(0x000000),
      });

      textBlock.add(text);
      textBlock.position.set(0.9, 0.31, -0.5 + index * 0.15);
      textBlock.rotation.x = -Math.PI * 0.5;
      textBlock.rotation.z = Math.PI * 0.5;

      // Store original states for textBlock
      button.userData.textBlock = textBlock;
      button.userData.textBlockOriginalScale = textBlock.scale.clone(); // Default is (1, 1, 1)
      button.userData.textBlockOriginalRotation = textBlock.rotation.clone();

      uiContainer.add(textBlock);
    });

    scene.add(uiContainer);

    if (chipButtons.length > 0) {
      selectChip(chipButtons[chipButtons.length - 1]);
    }
  },
  undefined,
  (error) => {
    console.error("Error loading coin model:", error);
  }
);

/**
 * Materials
 */
// Baked material
const bakedMaterial = new THREE.MeshBasicMaterial({ map: bakedTexture });
const portalLightMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
const poleLightMaterial = new THREE.MeshBasicMaterial({ color: 0xffffe5 });
/**
 * Base
 */
// Debug
const debugObject = {};

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Update all materials
 */
const updateAllMaterials = () => {
  scene.traverse((child) => {
    if (
      child instanceof THREE.Mesh &&
      child.material instanceof THREE.MeshStandardMaterial
    ) {
      // child.material.envMap = environmentMap
      child.material.envMapIntensity = debugObject.envMapIntensity;
      child.material.needsUpdate = true;
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
};

/**
 * Model
 */
let gltfScene = null;
let straightBettingAreas = [];
gltfLoader.load("RouletteTable.glb", (gltf) => {
  gltfScene = gltf.scene;
  gltf.scene.traverse((child) => {
    child.material = bakedMaterial;
    child.castShadow = true; // Cast shadows
    child.receiveShadow = true; // Receive shadows
  });

  scene.add(gltf.scene);

  gltf.scene.children[14].scale.set(0, 0, 0);
  gltf.scene.children[0].scale.set(0, 0, 0);
  gltf.scene.children[4].scale.set(0, 0, 0);
  // gltf.scene.children[8].scale.set(0, 0, 0);

  // console.log(gltf.scene)
  const child = gltf.scene.children[15];
  // child.position.set(-2.3,child.position.y, 2.05);

  gltf.scene.scale.set(1.55, 1.55, 1.55);
  gltf.scene.add(uiContainer);
  gltf.scene.add(PinModel);
  gltf.scene.add(PinModelStand);

  setupBettingAreas(gltf.scene);
  straightBettingAreas = bettingAreas.filter(
    (area) => area.userData.type === "straight"
  ); // Added to populate straightBettingAreas

  gsap.to(gltf.scene.children[0].rotation, {
    y: "+=" + Math.PI * 2, // Rotates 360 degrees (one full rotation)
    duration: 20, // Takes 20 seconds for one rotation (adjust for speed)
    repeat: -1, // Repeats infinitely
    ease: "none", // Ensures constant speed without acceleration
  });

  historyContainer.position.set(2, 0.5, 0);
  scene.add(historyContainer);
  scene.add(paytableContainer);
  scene.add(paytableCoin);
  // scene.add(points)
  scene.add(wheelContainer);

  updateHistoryDisplay();
});

// Turret Pin

const TurretHeadTexture = textureLoader.load("Turret2.jpg");
TurretHeadTexture.flipY = false;
TurretHeadTexture.colorSpace = THREE.SRGBColorSpace;

TurretHeadTexture.castShadow = true;
// Create a material using the texture
const TurretMaterial = new THREE.MeshBasicMaterial({ map: TurretHeadTexture });
let PinModel;

// Load the wheel model and add it to wheelContainer
const loadPin = new Promise((resolve, reject) => {
  gltfLoader.load(
    "Turret2.glb",
    (gltf) => {
      PinModel = gltf.scene;
      PinModel.traverse((child) => {
        if (child.isMesh) {
          child.material = TurretMaterial;
          child.castShadow = true; // Cast shadows
          child.receiveShadow = true; // Receive shadows
        }
      });
      PinModel.scale.set(1, 1, 1);
      PinModel.position.set(-0.195, 0.318, -0.389);
      // PinModel.rotation.x = -4.7;
      resolve(PinModel);
    },
    undefined,
    (error) => {
      reject(error);
    }
  );
});

loadPin.catch((error) => {
  console.error("Error loading wheel model:", error);
});

// Stand Pin

const TurretStandTexture = textureLoader.load("gold-l.jpg");
TurretStandTexture.flipY = true;
TurretStandTexture.colorSpace = THREE.SRGBColorSpace;

TurretStandTexture.castShadow = true;
// Create a material using the texture
const TurretStandMaterial = new THREE.MeshBasicMaterial({
  map: TurretStandTexture,
});
let PinModelStand;

// Load the wheel model and add it to wheelContainer
const loadPinStand = new Promise((resolve, reject) => {
  gltfLoader.load(
    "turrent_holder.glb",
    (gltf) => {
      PinModelStand = gltf.scene;
      PinModelStand.traverse((child) => {
        if (child.isMesh) {
          child.material = TurretStandMaterial;
          child.castShadow = true; // Cast shadows
          child.receiveShadow = true; // Receive shadows
        }
      });
      PinModelStand.scale.set(0.28, 0.28, 0.28);
      PinModelStand.position.set(-0.195, 0.312, -0.389);
      PinModelStand.rotation.x = -Math.PI / 2;
      resolve(PinModelStand);
    },
    undefined,
    (error) => {
      reject(error);
    }
  );
});

loadPinStand.catch((error) => {
  console.error("Error loading wheel model:", error);
});

/**
 * Lights
 */

// Ambient light (soft light)
// const ambientLight = new THREE.AmbientLight('red', 1);
// scene.add(ambientLight);

// // Directional light (stronger, more focused light)
// const directionalLight = new THREE.DirectionalLight('red', 1);
// directionalLight.position.set(5, 5, 5);  // Position the light
// directionalLight.castShadow = true; // Enable shadows for this light
// scene.add(directionalLight);

// Spotlight - Adjusted for better visibility
const spotLight = new THREE.SpotLight(0xffffff, 2); // Changed to white, increased intensity
spotLight.position.set(0, 2, 0);
spotLight.angle = Math.PI / 6;
spotLight.penumbra = 0.1;
spotLight.distance = 10;
spotLight.decay = 2;
spotLight.castShadow = true;
spotLight.shadow.mapSize.width = 1024;
spotLight.shadow.mapSize.height = 1024;

const spotLightTarget = new THREE.Object3D();
spotLightTarget.position.set(0, 0, 0);
spotLight.target = spotLightTarget;

scene.add(spotLight);
scene.add(spotLightTarget);

const points = [
  {
    position: new THREE.Vector3(-1.5, 0.78, -0.65),
    element: document.querySelector(".point-0"),
  },
];

// Chip Selection UI
const uiContainer = new THREE.Group();
const chipButtons = [];
const chipValues = [30, 25, 15, 10, 5];

if (!coinModel) {
} else {
  chipValues.forEach((value, index) => {
    const button = coinModel.clone();
    button.traverse((child) => {
      if (child.isMesh) {
        const texture = coinTextures[value] || coinTextures[5];
        child.material = new THREE.MeshBasicMaterial({ map: texture });
      }
    });

    button.scale.set(0.8, 0.8, 0.8);
    button.rotation.x = -Math.PI / 2;
    button.rotation.z = 1.6;
    button.position.y = 0.35 - index * 0.12;
    button.position.x = 1;
    button.position.z = -0.2;

    button.userData = { value: value };
    button.castShadow = true;

    uiContainer.add(button);
    chipButtons.push(button);

    const textBlock = new ThreeMeshUI.Block({
      width: 0.1,
      height: 0.05,
      justifyContent: "center",
      alignItems: "center",
      backgroundOpacity: 0,
      fontFamily:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
      fontTexture:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
    });

    const text = new ThreeMeshUI.Text({
      content: `$${value}`,
      fontSize: 0.04,
      fontColor: new THREE.Color(0xffffff),
    });

    textBlock.add(text);
    textBlock.position.set(1, 0.35 - index * 0.12 + 0.02, -0.2);
    textBlock.rotation.x = -Math.PI * 0.5;
    uiContainer.add(textBlock);
  });
}

uiContainer.position.set(0, 0, 0);
scene.add(uiContainer);

// Bet History
let betHistory = [];

let betHistoryDetails = [];
let currentBalance =
  parseFloat(
    document.querySelector("#userBalance h2").textContent.split(" ")[1]
  ) || 100;
const historyContainer = new THREE.Object3D();

function getColorClassForNumber(number) {
  const color = getColorForNumber(number);
  if (color === colorRed) return "red";
  if (color === colorBlack) return "black";
  if (color === colorGreen) return "green";
  return "unknown"; // Fallback, though unlikely with your colorMap
}

function updateHistoryDisplay() {
  // Clear existing children from historyContainer
  while (historyContainer.children.length > 0) {
    historyContainer.remove(historyContainer.children[0]);
  }

  const columns = 5; // 5 columns per row
  const columnSpacing = 0.17; // Horizontal spacing between columns
  const rowSpacing = -0.18; // Vertical spacing between rows (replacing the hardcoded 0.2)

  betHistory.forEach((number, index) => {
    // Calculate row and column based on index
    const columnIndex = index % columns; // Column: 0 to 4
    const rowIndex = Math.floor(index / columns); // Row: 0 or 1

    // Calculate positions for the grid
    const x = columnIndex * columnSpacing; // X-position (horizontal)
    const y = rowIndex * rowSpacing; // Y-position (vertical)
    const z = 0; // Z-position (simplified, no need for rowSpacing here)

    //  cylinder
    const geometry = new THREE.CylinderGeometry(0.06, 0.06, 0.005, 32);
    const BorderGeometry = new THREE.CylinderGeometry(0.06, 0.06, 0.008, 32);
    const material = new THREE.MeshBasicMaterial({
      color: getColorForNumber(number),
    });
    const cylinder = new THREE.Mesh(geometry, material);
    cylinder.position.set(x, y, z);
    cylinder.rotation.x = Math.PI / 2;
    cylinder.castShadow = true;
    historyContainer.add(cylinder);

    //  border
    const edges = new THREE.EdgesGeometry(BorderGeometry);
    const borderMaterial = new THREE.LineBasicMaterial({
      color: 0xbd8b28,
      backgroundOpacity: 0,
    });
    const border = new THREE.LineSegments(edges, borderMaterial);
    border.position.set(x, y, z);
    border.rotation.x = Math.PI / 2; // Match cylinder rotation
    historyContainer.add(border);

    // text label
    const textBlock = new ThreeMeshUI.Block({
      width: 0.1,
      height: 0.01,
      justifyContent: "center",
      alignItems: "center",
      fontFamily:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
      fontTexture:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
      backgroundOpacity: 0,
    });
    textBlock.add(
      new ThreeMeshUI.Text({
        content: number.toString(),
        fontSize: 0.049,
        color: new THREE.Color(0xffffff),
      })
    );
    textBlock.position.set(x, y + 0.005, -0.007);
    textBlock.rotation.x = Math.PI * 2;
    historyContainer.add(textBlock);
  });

  historyContainer.position.set(0.925, 0.82, 1.77);
  historyContainer.rotation.set(0.8, 3.15, 6.3);
}

// Paytable Container

const paytableCoin = new ThreeMeshUI.Block({
  width: 0.1,
  height: 0.1,
  borderRadius: 0.05,
  // backgroundColor: new THREE.Color(0xffffff),
  backgroundOpacity: 0,
});

textureLoader.load(
  "icons/Coin-pay.png",
  (texture) => {
    chipBlock.set({ backgroundTexture: texture });
  },
  undefined,
  (error) => {
    console.error("Error loading texture:", error);
  }
);

const chipBlock = new ThreeMeshUI.Block({
  width: 0.1,
  height: 0.1,
  borderRadius: 0.05,
  // background: CoinPay,
  backgroundOpacity: 1,
});

// Add to Scene
paytableCoin.position.set(-1.35, 0.77, -0.9);
paytableCoin.rotation.x = -Math.PI / 1.65;
paytableCoin.rotation.y = 0.7;
paytableCoin.rotation.z = 2.12;
scene.add(paytableCoin);

paytableCoin.add(chipBlock);

const paytableGHS = new ThreeMeshUI.Block({
  width: 0.1,
  height: 0.1,
  borderRadius: 0.05,
  backgroundColor: new THREE.Color(0xffffff),
  backgroundOpacity: 0,
});
const valueTextBlock = new ThreeMeshUI.Block({
  width: 0.3,
  height: 0.2,
  justifyContent: "center",
  alignItems: "center",
  backgroundOpacity: 0,
});
valueTextBlock.add(
  new ThreeMeshUI.Text({
    content: "= 1 EUR",
    fontSize: 0.075,
    fontColor: new THREE.Color(0xffffff),
    fontFamily:
      "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
    fontTexture:
      "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
  })
);

paytableGHS.add(valueTextBlock);
// Add to Scene
paytableGHS.position.set(-1.49, 0.79, -1.09);
paytableGHS.rotation.x = -Math.PI / 1.65;
paytableGHS.rotation.y = 0.7;
paytableGHS.rotation.z = 2.12;
scene.add(paytableGHS);

const paytableContainer = new ThreeMeshUI.Block({
  width: 0.6,
  height: 0.45,
  backgroundOpacity: 0,
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

// "PAYTABLE" Text
const paytableTextBlock = new ThreeMeshUI.Block({
  width: 0.45,
  height: 0.15,
  justifyContent: "center", // Center "PAYTABLE" horizontally
  alignItems: "center", // Center "PAYTABLE" vertically
  backgroundOpacity: 0,
});
paytableTextBlock.add(
  new ThreeMeshUI.Text({
    content: "PAYTABLE",
    fontSize: 0.086,
    fontColor: new THREE.Color("#f2ddbb"),
    fontFamily:
      "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
    fontTexture:
      "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
  })
);

// Add the wrapper and paytableTextBlock to paytableContainer
paytableContainer.add(paytableTextBlock);

// Add to Scene
paytableContainer.position.set(-1.3, 0.63, -1.11);
paytableContainer.rotation.x = -Math.PI / 1.65;
paytableContainer.rotation.y = 0.7;
paytableContainer.rotation.z = 2.12;
scene.add(paytableContainer);

/****************************************** Betting Areas Setup ************************************************/

let bettingAreas = [];
const colorRed = "#f8303d";
const colorBlack = "#16202c";
const colorGreen = "#1a6f46"; // Green for Zero
const colorGolden = "#BD8B28";

const colorMap = {
  1: colorRed,
  2: colorBlack,
  3: colorRed,
  4: colorBlack,
  5: colorRed,
  6: colorBlack,
  7: colorRed,
  8: colorBlack,
  9: colorRed,
  10: colorBlack,
  11: colorBlack,
  12: colorRed,
  13: colorBlack,
  14: colorRed,
  15: colorBlack,
  16: colorRed,
  17: colorBlack,
  18: colorRed,
  19: colorRed,
  20: colorBlack,
  21: colorRed,
  22: colorBlack,
  23: colorRed,
  24: colorBlack,
  25: colorRed,
  26: colorBlack,
  27: colorRed,
  28: colorBlack,
  29: colorBlack,
  30: colorRed,
  31: colorBlack,
  32: colorRed,
  33: colorBlack,
  34: colorRed,
  35: colorBlack,
  36: colorRed,
  37: colorBlack,
  38: colorRed,
  39: colorBlack,
  40: colorRed,
  41: colorRed,
  42: colorBlack,
  43: colorRed,
  44: colorBlack,
  45: colorRed,
  46: colorBlack,
  47: colorRed,
  48: colorBlack,
  49: colorBlack,
  50: colorRed,
  51: colorBlack,
  52: colorRed,
  53: colorBlack,
  54: colorRed,
  55: colorBlack,
  56: colorRed,
  57: colorBlack,
  58: colorRed,
  59: colorBlack,
  60: colorRed,
  0: colorGreen,
  "00": colorGreen,
};
function getColorForNumber(number) {
  if (number === "00") {
    return colorMap["00"]; // Handle '00' explicitly
  } else {
    const num = Number(number); // Convert other strings to numbers
    return colorMap[num] || 0xffffff;
  }
}

function setupBettingAreas(tableModel) {
  const spacingX = 0.25; // Spacing between columns
  const spacingZ = 0.14; // Spacing between rows
  const zOffset = -1.608; // Adjust this to shift the grid along the z-axis
  const columns = 3;

  for (let i = 1; i <= 60; i++) {
    const column = (i - 1) % columns;
    const row = Math.floor((i - 1) / columns);
    const number = 61 - i;
    const bgColor = getColorForNumber(number);

    const bettingArea = new THREE.Mesh(
      new THREE.PlaneGeometry(0.248, 0.138),
      new THREE.MeshBasicMaterial({
        color: bgColor,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
      })
    );

    const borderThickness = 0.002;
    const borderMaterial = new THREE.MeshBasicMaterial({ color: "white" });

    // Top border
    const topGeo = new THREE.BoxGeometry(
      0.25,
      borderThickness,
      borderThickness
    );
    const topBorder = new THREE.Mesh(topGeo, borderMaterial);
    topBorder.position.set(0, 0.07 + borderThickness / 2, 0);

    // Bottom border
    const bottomGeo = new THREE.BoxGeometry(
      0.25,
      borderThickness,
      borderThickness
    );
    const bottomBorder = new THREE.Mesh(bottomGeo, borderMaterial);
    bottomBorder.position.set(0, -0.07 - borderThickness / 2, 0);

    // Left border
    const leftGeo = new THREE.BoxGeometry(
      borderThickness,
      0.14,
      borderThickness
    );
    const leftBorder = new THREE.Mesh(leftGeo, borderMaterial);
    leftBorder.position.set(-0.125 - borderThickness / 2, 0, 0);

    // Right border
    const rightGeo = new THREE.BoxGeometry(
      borderThickness,
      0.14,
      borderThickness
    );
    const rightBorder = new THREE.Mesh(rightGeo, borderMaterial);
    rightBorder.position.set(0.125 + borderThickness / 2, 0, 0);

    // Group the borders
    const border = new THREE.Group();
    border.add(topBorder);
    border.add(bottomBorder);
    border.add(leftBorder);
    border.add(rightBorder);

    // Create overlay
    const overlayGeometry = new THREE.PlaneGeometry(0.25, 0.14);
    const overlayMaterial = new THREE.MeshBasicMaterial({
      color: "#fff",
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);

    // Position betting area, border, and overlay
    bettingArea.position.set(
      column * spacingX,
      0.483,
      zOffset + row * spacingZ
    );
    border.position.set(column * spacingX, 0.4835, zOffset + row * spacingZ);
    overlay.position.copy(bettingArea.position);
    overlay.position.y += 0.001; // Slightly above to avoid z-fighting

    // Rotate all to lie flat
    bettingArea.rotation.x = -Math.PI * 0.5;
    border.rotation.x = -Math.PI * 0.5;
    overlay.rotation.x = -Math.PI * 0.5;

    // Set up userData
    bettingArea.userData = {
      type: "straight",
      number: number,
      color: bgColor === colorRed ? "red" : "black",
      bets: [],
      chips: [],
      column: column, // Added
      border: border,
      overlay: overlay, // Store overlay reference
      originalColor: bettingArea.material.color.clone(),
    };

    // Add to scene
    scene.add(bettingArea);
    scene.add(border);
    scene.add(overlay); // Add overlay to scene
    bettingAreas.push(bettingArea);

    // Existing number text code...
    const numberBlock = new ThreeMeshUI.Block({
      width: 0.1,
      height: 0.05,
      justifyContent: "center",
      alignItems: "center",
      backgroundOpacity: 0,
      fontFamily:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
      fontTexture:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
    });

    const numberText = new ThreeMeshUI.Text({
      content: number.toString(),
      fontSize: 0.08,
      fontColor: new THREE.Color(0xffffff),
    });

    numberBlock.add(numberText);
    numberBlock.position.copy(bettingArea.position);
    numberBlock.position.y += -0.01;
    numberBlock.rotation.x = -Math.PI * 0.5;
    numberBlock.rotation.z = 3.15;

    scene.add(numberBlock);

    bettingArea.userData.originalColor = bettingArea.material.color.clone(); // Store original color
    bettingArea.userData.type = "straight";
    // bettingArea.userData.number = i;
  }

  // Zero Betting Area
  const zeroBet = createBettingArea(0.372, 0.14, colorGreen, "0", {
    type: "straight",
    number: 0,
    color: "green",
  });
  zeroBet.position.set(1.76 * spacingX, 0.483, zOffset + 20.05 * spacingZ); // Position to the left of the number grid
  scene.add(zeroBet);
  bettingAreas.push(zeroBet);
  addTextZero(zeroBet, "0");

  const zeroDoubleBet = createBettingArea(0.374, 0.14, colorGreen, "00", {
    type: "straight",
    number: 0,
    color: "green",
  });
  zeroDoubleBet.position.set(
    0.249 * spacingX,
    0.483,
    zOffset + 20.05 * spacingZ
  ); // Position to the left of the number grid
  scene.add(zeroDoubleBet);
  bettingAreas.push(zeroDoubleBet);
  addTextZero(zeroDoubleBet, "00");

  const splitZeroDoubleZero = createBettingArea1(0.05, 0.05, 0x555555, "0-00", {
    type: "split",
    value: "0-00",
  });
  splitZeroDoubleZero.position.set(
    (0.249 * spacingX + 1.76 * spacingX) / 2,
    0.49,
    zOffset + 20.05 * spacingZ
  );
  splitZeroDoubleZero.rotation.x = -Math.PI * 0.5;
  splitZeroDoubleZero.rotation.z = Math.PI * 0.5;
  splitZeroDoubleZero.material.opacity = 0;
  scene.add(splitZeroDoubleZero);
  bettingAreas.push(splitZeroDoubleZero);

  // Red/Black
  const redBet = createBettingArea(0.2, 0.584, colorRed, "red", {
    type: "redBet",
    value: "red",
  });
  redBet.position.set(3.7 * spacingX, 0.483, zOffset + 11.6 * spacingZ);
  scene.add(redBet);
  bettingAreas.push(redBet);
  addTextToBettingArea(redBet, "RED");

  const blackBet = createBettingArea(0.2, 0.579, colorBlack, "black", {
    type: "blackBet",
    value: "black",
  });
  blackBet.position.set(3.7 * spacingX, 0.483, zOffset + 7.4 * spacingZ);
  scene.add(blackBet);
  bettingAreas.push(blackBet);
  addTextToBettingArea(blackBet, "BLACK");

  // Odd/Even
  const oddBet = createBettingArea(0.2, 0.35, colorGolden, "odd", {
    type: "oddBet",
    value: "odd",
  });
  oddBet.position.set(3.702 * spacingX, 0.483, zOffset + 14.97 * spacingZ);
  scene.add(oddBet);
  bettingAreas.push(oddBet);
  addTextToBettingArea(oddBet, "ODD");

  const evenBet = createBettingArea(0.2, 0.348, colorGolden, "even", {
    type: "evenBet",
    value: "even",
  });
  evenBet.position.set(3.702 * spacingX, 0.483, zOffset + 4.038 * spacingZ);
  scene.add(evenBet);
  bettingAreas.push(evenBet);
  addTextToBettingArea(evenBet, "EVEN");

  // Low/High (1-18, 19-36)
  const lowBet = createBettingArea(0.2, 0.45, colorGolden, "1-30", {
    type: "lowBet",
    value: "1-30",
  });
  lowBet.position.set(3.7 * spacingX, 0.483, zOffset + 17.87 * spacingZ);
  scene.add(lowBet);
  bettingAreas.push(lowBet);
  addTextToBettingArea(lowBet, "1 - 30");

  const highBet = createBettingArea(0.2, 0.45, colorGolden, "31-60", {
    type: "highBet",
    value: "31-60",
  });
  highBet.position.set(3.7 * spacingX, 0.483, zOffset + 1.15 * spacingZ);
  scene.add(highBet);
  bettingAreas.push(highBet);
  addTextToBettingArea(highBet, "31 - 60");

  // Dozens (1-12, 13-24, 25-36, 37-48, 49-60)

  const dozen1Bet = createBettingArea(0.554, 0.19, colorGolden, "1-12", {
    type: "first12",
    value: "1-12",
  });
  dozen1Bet.position.set(2.9 * spacingX, 0.483, zOffset + 17.5 * spacingZ);
  dozen1Bet.rotation.z = Math.PI * 0.5;
  scene.add(dozen1Bet);
  bettingAreas.push(dozen1Bet);
  addTextToBettingArea(dozen1Bet, "1 - 12");

  const dozen2Bet = createBettingArea(0.554, 0.19, colorGolden, "13-24", {
    type: "second12",
    value: "13-24",
  });
  dozen2Bet.position.set(2.9 * spacingX, 0.483, zOffset + 13.5 * spacingZ);
  dozen2Bet.rotation.z = Math.PI * 0.5;
  scene.add(dozen2Bet);
  bettingAreas.push(dozen2Bet);
  addTextToBettingArea(dozen2Bet, "13 - 24");

  const dozen3Bet = createBettingArea(0.554, 0.19, colorGolden, "25-36", {
    type: "third12",
    value: "25-36",
  });
  dozen3Bet.position.set(2.9 * spacingX, 0.483, zOffset + 9.5 * spacingZ);
  dozen3Bet.rotation.z = Math.PI * 0.5;
  scene.add(dozen3Bet);
  bettingAreas.push(dozen3Bet);
  addTextToBettingArea(dozen3Bet, "25 - 36");

  const dozen4Bet = createBettingArea(0.554, 0.19, colorGolden, "37-48", {
    type: "fourth12",
    value: "37-48",
  });
  dozen4Bet.position.set(2.9 * spacingX, 0.483, zOffset + 5.51 * spacingZ);
  dozen4Bet.rotation.z = Math.PI * 0.5;
  scene.add(dozen4Bet);
  bettingAreas.push(dozen4Bet);
  addTextToBettingArea(dozen4Bet, "37 - 48");

  const dozen5Bet = createBettingArea(0.554, 0.19, colorGolden, "49-60", {
    type: "fifth12",
    value: "49-60",
  });
  dozen5Bet.position.set(2.9 * spacingX, 0.483, zOffset + 1.52 * spacingZ);
  dozen5Bet.rotation.z = Math.PI * 0.5;
  scene.add(dozen5Bet);
  bettingAreas.push(dozen5Bet);
  addTextToBettingArea(dozen5Bet, "49 - 60");

  // 2 to 1 Bets (Columns)
  const column1Bet = createBettingArea(
    0.25,
    0.14,
    colorBlack,
    "2 to 1 (Column 1)",
    { type: "thirdCol", value: "3" }
  );
  column1Bet.position.set(0 * spacingX, 0.485, zOffset - 1 * spacingZ); // At the bottom of column 1
  scene.add(column1Bet);
  bettingAreas.push(column1Bet);
  addTextcolumn(column1Bet, "2 to 1");

  const column2Bet = createBettingArea(
    0.24,
    0.14,
    colorBlack,
    "2 to 1 (Column 2)",
    { type: "secCol", value: "2" }
  );
  column2Bet.position.set(1 * spacingX, 0.485, zOffset - 1 * spacingZ); // At the bottom of column 2
  scene.add(column2Bet);
  bettingAreas.push(column2Bet);
  addTextcolumn(column2Bet, "2 to 1");

  const column3Bet = createBettingArea(
    0.25,
    0.14,
    colorBlack,
    "2 to 1 (Column 3)",
    { type: "firstCol", value: "1" }
  );
  column3Bet.position.set(2 * spacingX, 0.485, zOffset - 1 * spacingZ); // At the bottom of column 3
  scene.add(column3Bet);
  bettingAreas.push(column3Bet);
  addTextcolumn(column3Bet, "2 to 1");

  /**********************************betting area sets***************************************/

  // Horizontal Split Bets (e.g., 60-59, 59-58 in each row)
  for (let j = 0; j < 20; j++) {
    // Between columns 0 and 1 (e.g., 60-59 in row 0)
    let num1 = 60 - 3 * j - 0;
    let num2 = 60 - 3 * j - 1;
    let splitArea1 = createBettingArea1(
      0.05,
      0.05,
      0x555555,
      `${num1}-${num2}`,
      { type: "split", value: `${num1}-${num2}` }
    );
    splitArea1.position.set(0.5 * spacingX, 0.5, zOffset + j * spacingZ);
    splitArea1.rotation.z = Math.PI * 0.5;
    splitArea1.position.y = 0.49;
    splitArea1.material.opacity = 0;
    scene.add(splitArea1);
    bettingAreas.push(splitArea1);

    // Between columns 1 and 2 (e.g., 59-58 in row 0)
    let num3 = 60 - 3 * j - 1;
    let num4 = 60 - 3 * j - 2;
    let splitArea2 = createBettingArea1(
      0.05,
      0.05,
      0x555555,
      `${num3}-${num4}`,
      { type: "split", value: `${num3}-${num4}` }
    );
    splitArea2.position.set(1.5 * spacingX, 0.5, zOffset + j * spacingZ);
    splitArea2.rotation.z = Math.PI * 0.5;
    splitArea2.position.y = 0.49;
    splitArea2.material.opacity = 0;
    scene.add(splitArea2);
    bettingAreas.push(splitArea2);
  }

  // Vertical Split Bets (e.g., 60-57 in column 0)
  for (let j = 0; j < 19; j++) {
    for (let k = 0; k < 3; k++) {
      let num1 = 60 - 3 * j - k;
      let num2 = 60 - 3 * (j + 1) - k;
      let splitArea = createBettingArea1(
        0.05,
        0.05,
        0x555555,
        `${num1}-${num2}`,
        { type: "split", value: `${num1}-${num2}` }
      );
      splitArea.position.set(k * spacingX, 0.5, zOffset + (j + 0.5) * spacingZ);
      splitArea.rotation.z = 0;
      splitArea.position.y = 0.49;
      splitArea.material.opacity = 0;
      scene.add(splitArea);
      bettingAreas.push(splitArea);
    }
  }

  // Street Bets (e.g., 60-59-58 for each row)
  for (let j = 0; j < 20; j++) {
    let num1 = 60 - 3 * j - 0;
    let num2 = 60 - 3 * j - 1;
    let num3 = 60 - 3 * j - 2;
    let streetArea = createBettingArea1(
      0.1,
      0.05,
      0x555555,
      `${num1}-${num2}-${num3}`,
      { type: "street", value: `${num1}-${num2}-${num3}` }
    );
    streetArea.position.set(-0.5 * spacingX, 0.5, zOffset + j * spacingZ);
    streetArea.rotation.z = Math.PI * 0.5;
    streetArea.position.y = 0.49;
    streetArea.material.opacity = 0;
    scene.add(streetArea);
    bettingAreas.push(streetArea);
  }

  // Corner Bets (e.g., 60-59-57-56 between rows and columns)
  for (let j = 0; j < 19; j++) {
    for (let k = 0; k < 2; k++) {
      let num1 = 60 - 3 * j - k;
      let num2 = 60 - 3 * j - (k + 1);
      let num3 = 60 - 3 * (j + 1) - k;
      let num4 = 60 - 3 * (j + 1) - (k + 1);
      let cornerArea = createBettingArea1(
        0.05,
        0.05,
        0x555555,
        `${num1}-${num2}-${num3}-${num4}`,
        { type: "corner", value: `${num1}-${num2}-${num3}-${num4}` }
      );
      cornerArea.position.set(
        (k + 0.5) * spacingX,
        0.5,
        zOffset + (j + 0.5) * spacingZ
      );
      cornerArea.rotation.z = Math.PI * 0.5;
      cornerArea.position.y = 0.49;
      cornerArea.material.opacity = 0;
      scene.add(cornerArea);
      bettingAreas.push(cornerArea);
    }
  }

  // Double Street Bets (e.g., 60-59-58-57-56-55 across two rows)
  for (let j = 0; j < 19; j++) {
    let num1 = 60 - 3 * j - 0;
    let num2 = 60 - 3 * j - 1;
    let num3 = 60 - 3 * j - 2;
    let num4 = 60 - 3 * (j + 1) - 0;
    let num5 = 60 - 3 * (j + 1) - 1;
    let num6 = 60 - 3 * (j + 1) - 2;
    let doubleStreetArea = createBettingArea1(
      0.1,
      0.05,
      0x555555,
      `${num1}-${num2}-${num3}-${num4}-${num5}-${num6}`,
      {
        type: "doubleStreet",
        value: `${num1}-${num2}-${num3}-${num4}-${num5}-${num6}`,
      }
    );
    doubleStreetArea.position.set(
      -0.5 * spacingX,
      0.5,
      zOffset + (j + 0.5) * spacingZ
    );
    doubleStreetArea.rotation.z = Math.PI * 0.5;
    doubleStreetArea.position.y = 0.49;
    doubleStreetArea.material.opacity = 0;
    scene.add(doubleStreetArea);
    bettingAreas.push(doubleStreetArea);
  }

  /************************************************************************/

  // Helper function to create a betting area
  function createBettingArea(width, height, color, name, userData) {
    // Create the main betting area mesh
    const bettingArea = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      new THREE.MeshBasicMaterial({
        color: color,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
        depthWrite: false,
      })
    );

    // Add a border (optional, for visual distinction)
    const borderOpacity = userData.type === "custom" ? 0 : 1;
    const thickness = 0.005;
    const outerShape = new THREE.Shape();
    outerShape.moveTo(-width / 2 - thickness, -height / 2 - thickness);
    outerShape.lineTo(width / 2 + thickness, -height / 2 - thickness);
    outerShape.lineTo(width / 2 + thickness, height / 2 + thickness);
    outerShape.lineTo(-width / 2 - thickness, height / 2 + thickness);
    outerShape.lineTo(-width / 2 - thickness, -height / 2 - thickness);
    const innerShape = new THREE.Path();
    innerShape.moveTo(-width / 2, -height / 2);
    innerShape.lineTo(width / 2, -height / 2);
    innerShape.lineTo(width / 2, height / 2);
    innerShape.lineTo(-width / 2, height / 2);
    innerShape.lineTo(-width / 2, -height / 2);
    outerShape.holes.push(innerShape);

    const borderGeometry = new THREE.ShapeGeometry(outerShape);
    const borderMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      opacity: borderOpacity,
      transparent: true,
    });
    const border = new THREE.Mesh(borderGeometry, borderMaterial);
    border.raycast = () => {};
    bettingArea.add(border);

    // Create the overlay for the hover effect

    const overlayGeometry = new THREE.PlaneGeometry(width, height);
    const overlayMaterial = new THREE.MeshBasicMaterial({
      color: "#fff", // Yellow for hover effect (adjustable)
      transparent: true,
      opacity: 0, // Initially invisible
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
    overlay.position.copy(bettingArea.position);
    bettingArea.userData = { ...userData, overlay: overlay };
    bettingArea.add(overlay); // Add overlay to the scene

    // Attach userData to the betting area
    bettingArea.userData = {
      ...userData,
      name: name,
      originalColor: bettingArea.material.color.clone(),
      bets: [],
      chips: [],
      overlay: overlay, // Reference to the overlay for hover control
    };

    // Orient the betting area
    bettingArea.rotation.x = -Math.PI * 0.5;

    return bettingArea;
  }

  // Helper function to create a betting area
  function createBettingArea1(width, height, color, name, userData) {
    // Create the main betting area mesh
    const bettingArea = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      new THREE.MeshBasicMaterial({
        color: color,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
        depthWrite: false,
      })
    );

    // Add a border (optional, for visual distinction)
    const borderOpacity = 0;
    const thickness = 0.0;
    const outerShape = new THREE.Shape();
    outerShape.moveTo(-width / 2 - thickness, -height / 2 - thickness);
    outerShape.lineTo(width / 2 + thickness, -height / 2 - thickness);
    outerShape.lineTo(width / 2 + thickness, height / 2 + thickness);
    outerShape.lineTo(-width / 2 - thickness, height / 2 + thickness);
    outerShape.lineTo(-width / 2 - thickness, -height / 2 - thickness);
    const innerShape = new THREE.Path();
    innerShape.moveTo(-width / 2, -height / 2);
    innerShape.lineTo(width / 2, -height / 2);
    innerShape.lineTo(width / 2, height / 2);
    innerShape.lineTo(-width / 2, height / 2);
    innerShape.lineTo(-width / 2, -height / 2);
    outerShape.holes.push(innerShape);
    const borderGeometry = new THREE.ShapeGeometry(outerShape);
    const borderMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      opacity: borderOpacity,
      transparent: true,
    });
    const border = new THREE.Mesh(borderGeometry, borderMaterial);
    // bettingArea.add(border);

    // Create the overlay for the hover effect

    const overlayGeometry = new THREE.PlaneGeometry(width, height);
    const overlayMaterial = new THREE.MeshBasicMaterial({
      color: "#fff", // Yellow for hover effect (adjustable)
      transparent: true,
      opacity: 0, // Initially invisible
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
    overlay.position.copy(bettingArea.position);
    bettingArea.userData = { ...userData, overlay: overlay };
    bettingArea.add(overlay); // Add overlay to the scene

    // Attach userData to the betting area
    bettingArea.userData = {
      ...userData,
      name: name,
      originalColor: bettingArea.material.color.clone(),
      bets: [],
      chips: [],
      overlay: overlay, // Reference to the overlay for hover control
    };

    // Orient the betting area
    bettingArea.rotation.x = -Math.PI * 0.5;

    return bettingArea;
  }

  // Helper function to add text to betting areas
  function addTextToBettingArea(bettingArea, textContent) {
    const textBlock = new ThreeMeshUI.Block({
      width: 0.2,
      height: 0.1,
      justifyContent: "center",
      alignItems: "center",
      backgroundOpacity: 0,
      fontFamily:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
      fontTexture:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
    });

    const text = new ThreeMeshUI.Text({
      content: textContent,
      fontSize: 0.06,
      fontColor: new THREE.Color(0xffffff),
    });

    textBlock.add(text);
    textBlock.position.copy(bettingArea.position);
    textBlock.position.y += -0.01;
    textBlock.rotation.x = -Math.PI * 0.5;
    textBlock.rotation.z = 1.57;

    scene.add(textBlock);
  }

  // Helper function to add text to betting areas
  function addTextcolumn(bettingArea, textContent) {
    const textBlock = new ThreeMeshUI.Block({
      width: 0.2,
      height: 0.1,
      justifyContent: "center",
      alignItems: "center",
      backgroundOpacity: 0,
      fontFamily:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
      fontTexture:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
    });

    const text = new ThreeMeshUI.Text({
      content: textContent,
      fontSize: 0.06,
      fontColor: new THREE.Color(0xffffff),
    });

    textBlock.add(text);
    textBlock.position.copy(bettingArea.position);
    textBlock.position.y += -0.01;
    textBlock.rotation.x = -Math.PI * 0.5;
    textBlock.rotation.z = 3.15;

    scene.add(textBlock);
  }
  function addTextZero(bettingArea, textContent) {
    const textBlock = new ThreeMeshUI.Block({
      width: 0.2,
      height: 0.1,
      justifyContent: "center",
      alignItems: "center",
      backgroundOpacity: 0,
      fontFamily:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
      fontTexture:
        "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
    });

    const text = new ThreeMeshUI.Text({
      content: textContent,
      fontSize: 0.08,
      fontColor: new THREE.Color(0xffffff),
    });

    textBlock.add(text);
    textBlock.position.copy(bettingArea.position);
    textBlock.position.y += -0.01;
    textBlock.rotation.x = -Math.PI * 0.5;
    textBlock.rotation.z = 3.15;

    scene.add(textBlock);
  }
}

// Background Music
const backgroundMusic = new Audio("sound/bg-music.mp3");
backgroundMusic.loop = true;
backgroundMusic.play(); // Start playing the music automatically when the page loads
let isMusicPlaying = true; // Set the initial state to true since music is playing
const musicToggleButton = document.getElementById("music-toggle");
const musicIcon = document.getElementById("music-icon");
musicIcon.src = "icons/sound-on.png"; // Set the initial icon to indicate music is playing
musicToggleButton.setAttribute("aria-label", "Play Music"); // Set the initial aria-label

musicToggleButton.addEventListener("click", () => {
  if (isMusicPlaying) {
    backgroundMusic.play();
    musicIcon.src = "icons/sound-on.png";
    musicToggleButton.setAttribute("aria-label", "Play Music");
  } else {
    backgroundMusic.pause();
    musicIcon.src = "icons/sound-mute.png";
    musicToggleButton.setAttribute("aria-label", "Pause Music");
  }
  isMusicPlaying = !isMusicPlaying; // Toggle the state
});

/************************ Raycasting Setup  ************************/
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
let selectedChipValue = null;

let previouslySelectedChip = null; // Track the previously selected chip

function onTouchOrClick(event) {
  if (event.target.closest(".tab-main-outer")) {
    return; // Exit early if the click is on the tab container or its children
  }
  if (event.target.tagName === "BUTTON" || event.target.closest("button")) {
    return;
  }
  if (event.type === "touchend") {
    event.preventDefault();
  }
  let clientX, clientY;
  if (event.type === "touchend") {
    clientX = event.changedTouches[0].clientX;
    clientY = event.changedTouches[0].clientY;
  } else {
    clientX = event.clientX;
    clientY = event.clientY;
  }
  mouse.x = (clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
  // console.log('Touch/Click coords:', mouse.x, mouse.y);
  const uiIntersects = raycaster.intersectObjects(chipButtons, true);
  if (uiIntersects.length > 0) {
    // console.log('UI Intersects:', uiIntersects);
    const intersectedObject = uiIntersects[0].object;
    let button = intersectedObject;
    while (button && !button.userData.value) {
      button = button.parent;
    }
    if (button && button.userData.value) {
      selectChip(button);
      if (previouslySelectedChip && previouslySelectedChip !== button) {
        gsap.killTweensOf(previouslySelectedChip.scale);
        gsap.to(previouslySelectedChip.scale, {
          x: previouslySelectedChip.userData.originalScale.x,
          y: previouslySelectedChip.userData.originalScale.y,
          z: previouslySelectedChip.userData.originalScale.z,
          duration: 0.3,
          ease: "power2.out",
        });
        previouslySelectedChip.traverse((child) => {
          if (child.isMesh) {
            child.material.opacity =
              previouslySelectedChip.userData.originalOpacity;
          }
        });
        if (previouslySelectedChip.userData.textBlock) {
          gsap.killTweensOf(previouslySelectedChip.userData.textBlock.scale);
          gsap.to(previouslySelectedChip.userData.textBlock.scale, {
            x: previouslySelectedChip.userData.textBlockOriginalScale.x,
            y: previouslySelectedChip.userData.textBlockOriginalScale.y,
            z: previouslySelectedChip.userData.textBlockOriginalScale.z,
            duration: 0.3,
            ease: "power2.out",
          });
        }
      }
      selectedChipValue = button.userData.value;
      gsap.to(button.scale, {
        x: 1.0,
        y: 1.0,
        z: 1.0,
        duration: 0.5,
        ease: "elastic.out(1, 0.5)",
        repeat: -1,
        yoyo: true,
      });
      button.traverse((child) => {
        if (child.isMesh) {
          child.material.opacity = 0.8;
        }
      });
      if (button.userData.textBlock) {
        gsap.to(button.userData.textBlock.scale, {
          x: 1.18,
          y: 1.18,
          z: 1.18,
          duration: 0.5,
          ease: "elastic.out(1, 0.5)",
          repeat: -1,
          yoyo: true,
        });
      }
      previouslySelectedChip = button;
    }
    return;
  }
  const bettingIntersects = raycaster.intersectObjects(bettingAreas);
  if (bettingIntersects.length > 0) {
    // console.log('Betting Intersects:', bettingIntersects);
    const bettingArea = bettingIntersects[0].object;
    const totalBet = bettingArea.userData.bets
      ? bettingArea.userData.bets.reduce((sum, value) => sum + value, 0)
      : 0;
    const selectedNumberElement = document.getElementById("selected-number");
    const selectedColorElement = document.getElementById("selected-color");
    const totalBetElement = document.getElementById("total-bet");
    if (bettingArea.userData.type === "straight") {
      const number = bettingArea.userData.number;
      const color = bettingArea.userData.color;
      if (selectedNumberElement)
        selectedNumberElement.textContent = `Selected: ${number}`;
      if (selectedColorElement)
        selectedColorElement.textContent = `Color: ${color}`;
    } else if (bettingArea.userData.type === "column") {
      if (selectedNumberElement)
        selectedNumberElement.textContent = `Selected: ${bettingArea.userData.name}`;
      if (selectedColorElement)
        selectedColorElement.textContent = `Type: Column`;
    } else {
      if (selectedNumberElement)
        selectedNumberElement.textContent = `Selected: ${bettingArea.userData.name}`;
      if (selectedColorElement)
        selectedColorElement.textContent = `Type: ${bettingArea.userData.type}`;
    }
    if (totalBetElement)
      totalBetElement.textContent = `Total bet: $${totalBet}`;
    if (selectedChipValue) {
      placeChip(bettingArea, selectedChipValue);
      const updatedTotalBet = bettingArea.userData.bets.reduce(
        (sum, value) => sum + value,
        0
      );
      if (totalBetElement)
        totalBetElement.textContent = `Total bet: $${updatedTotalBet}`;
    }
  }
}

// Add event listeners for both click and touchend
window.addEventListener("click", onTouchOrClick);
window.addEventListener("touchend", onTouchOrClick);

// Updated selectChip function
function selectChip(button) {
  if (previouslySelectedChip && previouslySelectedChip !== button) {
    gsap.killTweensOf(previouslySelectedChip.scale);
    gsap.to(previouslySelectedChip.scale, {
      x: previouslySelectedChip.userData.originalScale.x,
      y: previouslySelectedChip.userData.originalScale.y,
      z: previouslySelectedChip.userData.originalScale.z,
      duration: 0.3,
      ease: "power2.out",
    });
    previouslySelectedChip.traverse((child) => {
      if (child.isMesh) {
        child.material.opacity =
          previouslySelectedChip.userData.originalOpacity;
      }
    });
    if (previouslySelectedChip.userData.textBlock) {
      gsap.killTweensOf(previouslySelectedChip.userData.textBlock.scale);
      gsap.to(previouslySelectedChip.userData.textBlock.scale, {
        x: previouslySelectedChip.userData.textBlockOriginalScale.x,
        y: previouslySelectedChip.userData.textBlockOriginalScale.y,
        z: previouslySelectedChip.userData.textBlockOriginalScale.z,
        duration: 0.3,
        ease: "power2.out",
      });
    }
  }

  selectedChipValue = button.userData.value;

  // Update cursor coin texture
  cursorCoin.traverse((child) => {
    if (child.isMesh) {
      const texture = coinTextures[selectedChipValue] || coinTextures[5];
      child.material = new THREE.MeshBasicMaterial({ map: texture });
      // console.log(child.material)
    }
  });

  // Update chip value text on cursorCoin
  if (cursorCoin.userData.chipValueText) {
    cursorCoin.userData.chipValueText.set({
      content: selectedChipValue.toString(),
    });
  } else {
    console.warn("chipValueText not found on cursorCoin");
  }

  gsap.to(button.scale, {
    x: 1.0,
    y: 1.0,
    z: 1.0,
    duration: 0.5,
    ease: "elastic.out(1, 0.5)",
    repeat: -1,
    yoyo: true,
  });
  button.traverse((child) => {
    if (child.isMesh) {
      child.material.opacity = 0.8;
    }
  });
  if (button.userData.textBlock) {
    gsap.to(button.userData.textBlock.scale, {
      x: 1.18,
      y: 1.18,
      z: 1.18,
      duration: 0.5,
      ease: "elastic.out(1, 0.5)",
      repeat: -1,
      yoyo: true,
    });
  }

  previouslySelectedChip = button;
}

function getNumbersForBet(type, value) {
  const numbers = [];
  if (type === "redBet") {
    for (let i = 0; i <= 60; i++) {
      if (getColorForNumber(i) === colorRed) numbers.push(i);
    }
  } else if (type === "blackBet") {
    for (let i = 0; i <= 60; i++) {
      if (getColorForNumber(i) === colorBlack) numbers.push(i);
    }
  } else if (type === "oddBet") {
    for (let i = 1; i <= 60; i++) {
      if (i % 2 === 1) numbers.push(i);
    }
  } else if (type === "evenBet") {
    for (let i = 1; i <= 60; i++) {
      if (i % 2 === 0) numbers.push(i);
    }
  } else if (type === "lowBet") {
    if (value === "1-30") for (let i = 1; i <= 30; i++) numbers.push(i);
  } else if (type === "highBet") {
    if (value === "31-60") for (let i = 31; i <= 60; i++) numbers.push(i);
  } else if (type === "first12") {
    const ranges = { "1-12": [1, 12] };
    if (ranges[value]) {
      const [start, end] = ranges[value];
      for (let i = start; i <= end; i++) numbers.push(i);
    }
  } else if (type === "second12") {
    const ranges = { "13-24": [13, 24] };
    if (ranges[value]) {
      const [start, end] = ranges[value];
      for (let i = start; i <= end; i++) numbers.push(i);
    }
  } else if (type === "third12") {
    const ranges = { "25-36": [25, 36] };
    if (ranges[value]) {
      const [start, end] = ranges[value];
      for (let i = start; i <= end; i++) numbers.push(i);
    }
  } else if (type === "fourth12") {
    const ranges = { "37-48": [37, 48] };
    if (ranges[value]) {
      const [start, end] = ranges[value];
      for (let i = start; i <= end; i++) numbers.push(i);
    }
  } else if (type === "fifth12") {
    const ranges = { "49-60": [49, 60] };
    if (ranges[value]) {
      const [start, end] = ranges[value];
      for (let i = start; i <= end; i++) numbers.push(i);
    }
  } else if (type === "firstCol") {
    const colIndex = 2;
    straightBettingAreas.forEach((area) => {
      if (area.userData.column === colIndex) numbers.push(area.userData.number);
    });
  } else if (type === "secCol") {
    const colIndex = 1;
    straightBettingAreas.forEach((area) => {
      if (area.userData.column === colIndex) numbers.push(area.userData.number);
    });
  } else if (type === "thirdCol") {
    const colIndex = 0;
    straightBettingAreas.forEach((area) => {
      if (area.userData.column === colIndex) numbers.push(area.userData.number);
    });
  } else if (
    type === "split" ||
    type === "street" ||
    type === "corner" ||
    type === "doubleStreet"
  ) {
    numbers.push(...value.split("-").map(Number));
  }
  return numbers;
}

function getStraightAreasForNumbers(numbers) {
  return straightBettingAreas.filter((area) =>
    numbers.includes(area.userData.number)
  );
}

function highlightBettingArea(bettingArea, highlight) {
  if (bettingArea.userData.overlay) {
    bettingArea.userData.overlay.material.opacity = highlight ? 0.3 : 0;
  }

  // Optional: Keep border highlighting if desired
  // if (bettingArea.userData.border) {
  //     bettingArea.userData.border.material.color.set(highlight ? '#fff' : '#fff');
  // }
}

let currentHighlighted = []; // Track currently highlighted areas

// Updated onMouseMove function
function onMouseMove(event) {
  let clientX, clientY;
  if (event.type === "touchmove") {
    clientX = event.touches[0].clientX;
    clientY = event.touches[0].clientY;
  } else {
    clientX = event.clientX;
    clientY = event.clientY;
  }
  mouse.x = (clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);

  currentHighlighted.forEach((area) => highlightBettingArea(area, false));
  currentHighlighted = [];

  const intersects = raycaster.intersectObjects(bettingAreas);
  if (intersects.length > 0 && selectedChipValue) {
    const hoveredArea = intersects[0].object;
    const intersectionPoint = intersects[0].point;

    cursorCoin.position.copy(intersectionPoint);
    cursorCoin.position.y += 0.01;
    cursorCoin.rotation.x = 0;
    cursorCoin.visible = true;

    highlightBettingArea(hoveredArea, true);
    currentHighlighted.push(hoveredArea);
    if (hoveredArea.userData.type !== "straight") {
      const numbers = getNumbersForBet(
        hoveredArea.userData.type,
        hoveredArea.userData.value
      );
      const areasToHighlight = getStraightAreasForNumbers(numbers);
      areasToHighlight.forEach((area) => {
        highlightBettingArea(area, true);
        currentHighlighted.push(area);
      });
    }
  } else {
    cursorCoin.visible = false;
  }

  // Hide default cursor when cursorCoin is active
  if (cursorCoin.visible) {
    canvas.style.cursor = "none";
  } else {
    canvas.style.cursor = "default";
  }
}

// Updated mouseout event listener
canvas.addEventListener("mouseout", () => {
  cursorCoin.visible = false;
  canvas.style.cursor = "default";
});

function addButtonListeners(buttonId, callback) {
  const button = document.getElementById(buttonId);
  if (!button) {
    console.warn(`Button with ID ${buttonId} not found`);
    return;
  }
  const handler = (event) => {
    if (event.type === "touchend") {
      event.preventDefault();
    }
    // console.log(`Button ${buttonId} triggered`);
    callback(event);
  };
  button.addEventListener("click", handler);
  button.addEventListener("touchend", handler);
}

// Add event listener
window.addEventListener("mousemove", onMouseMove, false);

//reset the bets
function resetBets() {
  // Loop through all betting areas and remove the chips
  scene.children.forEach((object) => {
    if (object.userData && object.userData.chips) {
      // Remove each chip group from the scene
      object.userData.chips.forEach((chipGroup) => {
        scene.remove(chipGroup);
      });

      // Clear the stored chips and bets
      object.userData.chips = [];
      object.userData.bets = [];
    }
  });
}
//undo bets
function undoLastBet() {
  let lastBettingArea = null;

  // Iterate through betting areas in reverse order to find the most recent bet
  for (let i = scene.children.length - 1; i >= 0; i--) {
    let object = scene.children[i];
    if (
      object.userData &&
      object.userData.bets &&
      object.userData.bets.length > 0
    ) {
      lastBettingArea = object;
      break;
    }
  }

  if (lastBettingArea) {
    // Remove the last placed bet and its associated chip
    const lastChipGroup = lastBettingArea.userData.chips.pop();
    lastBettingArea.userData.bets.pop();

    if (lastChipGroup) {
      scene.remove(lastChipGroup);
    }
  }
}

let lastbetHistory = [];

function updateBetHistory(data) {
  const betHistoryDiv = document.getElementById("bet-history");
  betHistoryDiv.innerHTML = "";

  lastbetHistory.push({
    winningNumber: data.winningNumber,
    payout: data.totalPayout,
    bets: data.bets,
    timestamp: new Date().toLocaleString(),
  });

  if (lastbetHistory.length > 10) {
    lastbetHistory.shift();
  }

  // Loop through the last 10 bet rounds
  lastbetHistory.forEach((historyItem, index) => {
    const groupDiv = document.createElement("div");
    groupDiv.classList.add("bet-group");

    // Create dropdown button
    const dropdownButton = document.createElement("button");
    dropdownButton.classList.add("dropdown-button");
    dropdownButton.textContent = `Bet Round - ${index + 1} (${
      historyItem.timestamp
    })`;
    dropdownButton.onclick = function () {
      betList.style.display =
        betList.style.display === "none" ? "block" : "none";
    };

    // Create a container for all bets in the same round
    const betList = document.createElement("div");
    betList.classList.add("bet-list");
    betList.style.display = "none";

    historyItem.bets.forEach((bet) => {
      const betItem = document.createElement("div");
      betItem.classList.add("history-item");

      const typeSpan = document.createElement("p");
      typeSpan.textContent = bet.betType;

      // Get the color for the specific winning number
      let specificColor = getColorForNumber(historyItem.winningNumber);

      // Creating the number element with its specific background color
      const numberSpan = document.createElement("p");
      numberSpan.classList.add("number");
      numberSpan.textContent = historyItem.winningNumber;
      numberSpan.style.backgroundColor = specificColor;

      // Creating bet amount element
      const betSpan = document.createElement("p");
      betSpan.classList.add("bet");
      betSpan.textContent = ` ${bet.amount} EUR`;

      // Calculate profit/loss for this specific bet
      const profit = bet.win ? bet.payout : -bet.amount;
      const profitSpan = document.createElement("p");
      profitSpan.classList.add("profit");
      profitSpan.textContent = `${profit >= 0 ? "+" : ""}${profit}`;
      profitSpan.style.color = profit >= 0 ? "green" : "red";

      // Append all elements
      betItem.appendChild(numberSpan);
      betItem.appendChild(typeSpan);
      betItem.appendChild(betSpan);
      betItem.appendChild(profitSpan);

      // Append betItem to betList
      betList.appendChild(betItem);
    });

    // Append button and betList to groupDiv
    groupDiv.appendChild(dropdownButton);
    groupDiv.appendChild(betList);

    // Append the grouped bets container to history div
    betHistoryDiv.appendChild(groupDiv);
  });
}

// Add this at the top of your file (global scope)
let currentbalance = localStorage.getItem("userCash")
  ? parseInt(localStorage.getItem("userCash"))
  : 1000;

// Function to update cash in localStorage and UI
function updateCashDisplay(amount) {
  currentbalance = amount;
  localStorage.setItem("userCash", currentbalance);
  document.querySelector(
    "#userBalance h2"
  ).textContent = `Cash: ${currentbalance} EUR`;
}

// Initialize cash display when page loads
document.addEventListener("DOMContentLoaded", () => {
  updateCashDisplay(currentbalance);
});
// Modify the getBetsArray function to include spinWheel
async function getBetsArray() {
  if (isBallSpinning) {
    // console.log('Bet blocked: Wheel is currently spinning');
    alert("Please wait for the current spin to complete.");
    return;
  }

  let bets = [];

  const bettingAreasWithBets = bettingAreas.filter(
    (area) => area.userData.bets.length > 0
  );

  bettingAreasWithBets.forEach((area) => {
    let betType;
    let buttons = [];

    switch (area.userData.type) {
      case "straight":
        betType = "singleBet";
        buttons = [area.userData.number];
        break;
      case "split":
        betType = "splitBet";
        buttons = area.userData.value
          .split("-")
          .map((num) => (num === "00" ? "00" : Number(num)));
        break;
      case "street":
        betType = "streetBet";
        buttons = area.userData.value.split("-").map(Number);
        break;
      case "corner":
        betType = "cornerBet";
        buttons = area.userData.value.split("-").map(Number);
        break;
      case "doubleStreet":
        betType = "lineBet";
        buttons = area.userData.value.split("-").map(Number);
        break;
      case "redBet":
        betType = "redBet";
        buttons = getNumbersForBet("redBet", area.userData.value);
        break;
      case "blackBet":
        betType = "blackBet";
        buttons = getNumbersForBet("blackBet", area.userData.value);
        break;
      case "oddBet":
        betType = "oddBet";
        buttons = getNumbersForBet("oddBet", area.userData.value);
        break;
      case "evenBet":
        betType = "evenBet";
        buttons = getNumbersForBet("evenBet", area.userData.value);
        break;
      case "lowBet":
        betType = "lowBet";
        buttons = getNumbersForBet("lowBet", area.userData.value);
        break;
      case "highBet":
        betType = "highBet";
        buttons = getNumbersForBet("highBet", area.userData.value);
        break;
      case "first12":
        betType = "first12";
        buttons = getNumbersForBet("first12", area.userData.value);
        break;
      case "second12":
        betType = "second12";
        buttons = getNumbersForBet("second12", area.userData.value);
        break;
      case "third12":
        betType = "third12";
        buttons = getNumbersForBet("third12", area.userData.value);
        break;
      case "fourth12":
        betType = "fourth12";
        buttons = getNumbersForBet("fourth12", area.userData.value);
        break;
      case "fifth12":
        betType = "fifth12";
        buttons = getNumbersForBet("fifth12", area.userData.value);
        break;
      case "firstCol":
        betType = "firstCol";
        buttons = getNumbersForBet("firstCol", area.userData.value);
        break;
      case "secCol":
        betType = "secCol";
        buttons = getNumbersForBet("secCol", area.userData.value);
        break;
      case "thirdCol":
        betType = "thirdCol";
        buttons = getNumbersForBet("thirdCol", area.userData.value);
        break;
      default:
        betType = "unknownBet";
        buttons = [];
    }

    const amount = area.userData.bets.reduce((sum, val) => sum + val, 0);
    bets.push({ betType, button: buttons, amount });
  });

  if (bets.length === 0) {
    alert("No bets placed!");
    return;
  }

  debugObject1.bet_placed_camera();
  // https://betcom-roulette.studioubique-dev.com
  try {
    const response = await fetch(`${apiUrl}/Staticbets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bets }),
    });
    const data = await response.json();
    console.log("--", data);
    if (response.ok) {
      const winningNumber = data.winningNumber; // Keep as string
      let searchValue = winningNumber === "00" ? "00" : Number(winningNumber);
      const winningNumberIndex = numberSequence.indexOf(searchValue);

      if (winningNumberIndex === -1) {
        console.error(
          `Winning number ${winningNumber} not found in numberSequence`
        );
        alert(`Error: Invalid winning number ${winningNumber}`);
        debugObject1.default_camera();
        return;
      }

      spinWheel(winningNumberIndex, (resultNumber) => {
        // console.log('Spin callback executed for resultNumber:', resultNumber);
        const profit = data.balance - currentBalance;
        currentBalance = data.balance;
        updateCashDisplay(data.balance);

        betHistory.push(winningNumber);
        if (betHistory.length > 10) {
          betHistory.shift();
        }

        betHistoryDetails.push({
          number: winningNumber,
          totalBet: bets.reduce((sum, bet) => sum + bet.amount, 0),
          profit: profit,
        });
        if (betHistoryDetails.length > 10) {
          betHistoryDetails.shift();
        }

        updateHistoryDisplay();
        updateBetHistory(data);

        document.querySelector(
          "#userBalance h2"
        ).textContent = `Cash: ${data.balance} EUR`;

        const betPopup = document.querySelector(".bet-popup-main");
        const spanElement = document.querySelector(".bet-number-spin span");
        const pElement = document.querySelector(".bet-popup-inner p");
        const h3Element = document.querySelector(".bet-popup-inner h3");

        const isWinner = data.bets.some((bet) => bet.win);
        if (spanElement) spanElement.textContent = winningNumber;
        if (pElement) {
          pElement.textContent = isWinner ? `You Won!` : `You Lose!`;
          h3Element.textContent = isWinner
            ? `€${data.totalProfitOrLoss}`
            : `€${data.totalProfitOrLoss}`;

          h3Element.style.color = isWinner ? "green" : "red";
        }

        function showPopup() {
          if (betPopup) {
            betPopup.style.transform = "scale(1)";
            betPopup.style.transition = "transform 0.3s ease-in-out";
            setTimeout(() => {
              betPopup.style.transform = "scale(0)";
            }, 3000);
          }
        }

        showPopup();
        resetBets();
        debugObject1.default_camera();
      });
    } else {
      console.error("Server error:", data.error || "Unknown error");
      alert(data.error || "Server error occurred");
      debugObject1.default_camera();
    }
  } catch (err) {
    console.error("Failed to connect to the server:", err);
    alert("Failed to connect to the server.");
    debugObject1.default_camera();
  }
}

document.getElementById("addBet").onclick = function () {
  getBetsArray();
};
document.getElementById("resetButton").onclick = function () {
  resetBets();
};
document.getElementById("undoBet").onclick = function () {
  undoLastBet();
};

/*********************Spin Wheel**********************/

// texture wheel
const WheelSepColorTexture = textureLoader.load("gold-l.jpg");
const WheelCircleColorTexture = textureLoader.load("gold-c.jpg");
const WheelSepARMTexture = textureLoader.load(
  "./wheel/Poliigon_MetalGoldPaint_7253_Roughness.jpg"
);
const WheelSepNormalTexture = textureLoader.load(
  "./wheel/Poliigon_MetalGoldPaint_7253_Normal.jpg"
);
const WheelSepDisplacementTexture = textureLoader.load(
  "./wheel/Poliigon_MetalGoldPaint_7253_Metallic.jpg"
);

WheelSepColorTexture.colorSpace = THREE.SRGBColorSpace;
WheelCircleColorTexture.colorSpace = THREE.SRGBColorSpace;

// Shuffle function for randomizing numberSequence
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// Wheel constants
const wheelRadius = 0.35;
const wheelHeight = 0.1;
const segments = 62; // 0, 00, 1-60
const maxSpinSpeed = Math.PI * 5;
const slotAngle = (2 * Math.PI) / segments; // ~0.101 rad
const wheelRotationSpeed = (2 * Math.PI) / 20; // 2π rad every 20 seconds (~0.314 rad/s)

// Function to generate the number sequence
function generateNumberSequence() {
  // Step 1: Collect red and black numbers
  const redNumbers = [];
  const blackNumbers = [];
  for (let i = 1; i <= 60; i++) {
    if (colorMap[i] === colorRed) redNumbers.push(i);
    else if (colorMap[i] === colorBlack) blackNumbers.push(i);
  }

  // Step 2: Shuffle both arrays
  shuffleArray(redNumbers);
  shuffleArray(blackNumbers);

  // Step 3: Create first section (positions 1-30, starts with black)
  const firstSection = [];
  for (let i = 0; i < 15; i++) {
    firstSection.push(blackNumbers[i]); // Black
    firstSection.push(redNumbers[i]); // Red
  }

  // Step 4: Create second section (positions 32-61, starts with red)
  const secondSection = [];
  for (let i = 15; i < 30; i++) {
    secondSection.push(redNumbers[i]); // Red
    secondSection.push(blackNumbers[i]); // Black
  }

  // Step 5: Assemble full sequence
  return [0, ...firstSection, "00", ...secondSection];
}

// Example usage
let numberSequence = generateNumberSequence();
// console.log('numberSequence:', numberSequence); // Debug: log sequence

// Wheel container
const wheelContainer = new THREE.Group();
wheelContainer.position.set(-1.55, 0.54, 0.335);
wheelContainer.rotation.x = -Math.PI * 0.5;
scene.add(wheelContainer);

// Slot materials
const slotMaterials = {};
Object.keys(colorMap).forEach((number) => {
  slotMaterials[number] = new THREE.MeshBasicMaterial({
    color: colorMap[number],
  });
});
slotMaterials["00"] = new THREE.MeshBasicMaterial({ color: colorMap[0] }); // Use 0’s color for 00

// Create wheel slots
for (let i = 0; i < segments; i++) {
  const number = numberSequence[i];
  const angle = i * slotAngle;

  // Slot

  // number bg color

  // Define the 2D trapezoid shape
  const trapezoidShapeInner = new THREE.Shape();
  trapezoidShapeInner.moveTo(-0.02, -0.07); // Bottom-left
  trapezoidShapeInner.lineTo(0.02, -0.07); // Bottom-right
  trapezoidShapeInner.lineTo(0.014, 0.074); // Top-right
  trapezoidShapeInner.lineTo(-0.014, 0.074); // Top-left
  trapezoidShapeInner.lineTo(-0.02, -0.07); // Close the shape by returning to the starting point

  // Extrude the shape to give it depth
  const extrudeSettingsInner = {
    depth: 0.00001,
    bevelEnabled: false, // No bevel for a sharp edge
  };

  const slotGeometryInner = new THREE.ExtrudeGeometry(
    trapezoidShapeInner,
    extrudeSettingsInner
  );

  // Create the mesh
  const slotNumber = new THREE.Mesh(
    slotGeometryInner,
    slotMaterials[number] || slotMaterials[0]
  );

  // Position the slot (same as your original code)
  slotNumber.position.set(
    (wheelRadius - 0.01) * Math.cos(angle),
    (wheelRadius - 0.01) * Math.sin(angle),
    wheelHeight / 1 - 0.07
  );
  slotNumber.rotation.z = angle + Math.PI / 2;
  slotNumber.rotateX(Math.PI / -10);
  slotNumber.userData = { number: number, index: i };
  wheelContainer.add(slotNumber);

  // inner color layer
  const trapezoidShape = new THREE.Shape();
  trapezoidShape.moveTo(-0.02, -0.07); // Bottom-left
  trapezoidShape.lineTo(0.02, -0.07); // Bottom-right
  trapezoidShape.lineTo(0.01, 0.07); // Top-right
  trapezoidShape.lineTo(-0.01, 0.07); // Top-left
  trapezoidShape.lineTo(-0.02, -0.07); // Close the shape by returning to the starting point

  // Extrude the shape to give it depth
  const extrudeSettings = {
    depth: 0.00001,
    bevelEnabled: false, // No bevel for a sharp edge
  };

  const slotGeometry = new THREE.ExtrudeGeometry(
    trapezoidShape,
    extrudeSettings
  );

  // Create the mesh
  const slot = new THREE.Mesh(
    slotGeometry,
    slotMaterials[number] || slotMaterials[0]
  );

  // Position the slot (same as your original code)
  slot.position.set(
    (wheelRadius - 0.061) * Math.cos(angle),
    (wheelRadius - 0.061) * Math.sin(angle),
    wheelHeight / 2 - 0.02
  );
  slot.rotation.z = angle + Math.PI / 2;
  slot.userData = { number: number, index: i };
  wheelContainer.add(slot);
  // console.log(`Slot ${i}: number ${number}, angle ${(angle).toFixed(3)}`); // Debug: log slot assignments

  // Separator
  const separatorGeometry = new THREE.BoxGeometry(
    0.005,
    wheelHeight * 0.9,
    0.01
  );
  const separatorMaterial = new THREE.MeshBasicMaterial({
    map: WheelSepColorTexture,
    color: "gold",
  });
  const separator = new THREE.Mesh(separatorGeometry, separatorMaterial);
  separator.position.set(
    (wheelRadius - 0.04) * Math.cos(angle + slotAngle / 2),
    (wheelRadius - 0.04) * Math.sin(angle + slotAngle / 2),
    wheelHeight / 2 - 0.02
  );
  separator.rotation.z = angle + slotAngle / 2 + Math.PI / 2;

  wheelContainer.add(separator);

  // Number text
  const textBlock = new ThreeMeshUI.Block({
    width: 0.07,
    height: 0.04,
    justifyContent: "center",
    alignItems: "center",
    backgroundOpacity: 0,
    // backgroundColor: new THREE.Color(colorMap[number]),
    fontFamily:
      "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
    fontTexture:
      "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
  });

  textBlock.add(
    new ThreeMeshUI.Text({
      content: number.toString(),
      fontSize: 0.026,
      fontColor: new THREE.Color(0xffffff),
    })
  );

  textBlock.position.set(
    (wheelRadius + 0.035) * Math.cos(angle),
    (wheelRadius + 0.035) * Math.sin(angle),
    wheelHeight / 2 - 0.015
  );
  textBlock.rotation.z = angle - Math.PI / 2;
  textBlock.rotateX(Math.PI / 10);
  wheelContainer.add(textBlock);
}

const outerRadius = 0.36; // Matches original radius
const innerRadius = outerRadius - 0.015; // Radial thickness of the border
const shape = new THREE.Shape();
shape.absarc(0, 0, outerRadius, 0, Math.PI * 2, false); // Outer circle
const hole = new THREE.Path();
hole.absarc(0, 0, innerRadius, 0, Math.PI * 2, false); // Inner circle (hole)
shape.holes.push(hole);

const extrudeSettings = {
  steps: 1,
  depth: 0.01, // Depth of the border (Z-axis extent)
  bevelEnabled: false, // No bevel for a sharp edge
};
const borderInnerGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

// Create the material (from prior context)
const borderInnerMaterial = new THREE.MeshStandardMaterial({
  // color: 'gold',
  map: WheelCircleColorTexture,
  side: THREE.DoubleSide, // Ensure visibility from both sides
});

// Create the mesh
const borderInner = new THREE.Mesh(borderInnerGeometry, borderInnerMaterial);
borderInner.position.z = 0.026;
wheelContainer.add(borderInner);

// Ball
const ballGeometry = new THREE.SphereGeometry(0.014, 14, 14);
const ballMaterial = new THREE.MeshStandardMaterial({
  color: "white",
  // metalness: 0,
  // roughness: 0
});
const ball = new THREE.Mesh(ballGeometry, ballMaterial);
ball.position.set(wheelRadius + 0.08, 0, wheelHeight / 2 + 0.02);
ball.castShadow = true;
wheelContainer.add(ball);

// // Marker
// const markerGeometry = new THREE.ConeGeometry(0.05, 0.1, 8);
// const markerMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
// const marker = new THREE.Mesh(markerGeometry, markerMaterial);
// marker.position.set(0, wheelRadius + 0.1, wheelHeight / 2 + 0.05);
// marker.rotation.x = Math.PI;
// wheelContainer.add(marker);

// Lighting
const wheelAmbientLight = new THREE.AmbientLight(0xffffff, 1);
scene.add(wheelAmbientLight);

const wheelDirectionalLight = new THREE.DirectionalLight(0xffffff, 1.3);
wheelDirectionalLight.position.set(-1.5, 2, 1);
wheelDirectionalLight.castShadow = true;
scene.add(wheelDirectionalLight);

const ballPointLight = new THREE.PointLight(0xffffff, 1.8, 0.3);
ballPointLight.position.set(wheelRadius + 0.08, 0, wheelHeight / 2 + 0.07);
scene.add(ballPointLight);

// Start constant wheel rotation
const wheelRotationTween = gsap.to(wheelContainer.rotation, {
  z: "+=" + Math.PI * 2,
  duration: 20,
  repeat: -1,
  ease: "none",
});

// spin Wheel Object

const WheelHeadTexture = textureLoader.load("GoldWheel.jpg");
WheelHeadTexture.flipY = false;
WheelHeadTexture.colorSpace = THREE.SRGBColorSpace;

// Create a material using the texture
const wheelMaterial = new THREE.MeshBasicMaterial({ map: WheelHeadTexture });

let wheelModel;

// Load the wheel model and add it to wheelContainer
const loadWheel = new Promise((resolve, reject) => {
  gltfLoader.load(
    "GoldWheel.glb",
    (gltf) => {
      wheelModel = gltf.scene;
      wheelModel.traverse((child) => {
        if (child.isMesh) {
          child.material = wheelMaterial;
        }
      });
      wheelModel.scale.set(1.55, 1.55, 1.55);
      wheelModel.position.set(0, 0, 0.032);
      wheelModel.rotation.x = -4.7;
      wheelContainer.add(wheelModel);
      resolve(wheelModel);
    },
    undefined,
    (error) => {
      reject(error);
    }
  );
});

loadWheel.catch((error) => {
  console.error("Error loading wheel model:", error);
});

// Spin wheel function (controls ball only)
ball.visible = false;
let isBallSpinning = false;
function spinWheel(winningNumberIndex, callback) {
  if (isBallSpinning) {
    return;
  }
  isBallSpinning = true;

  ball.visible = true;
  gsap.to(wheelRotationTween, {
    timeScale: 5,
    duration: 1,
    ease: "power2.out",
  });

  const winningNumber = numberSequence[winningNumberIndex].toString();
  ball.position.set(wheelRadius + 0.08, 0, wheelHeight / 2 + 0.02);
  ballPointLight.position.set(wheelRadius + 0.08, 0, wheelHeight / 2 + 0.07);
  gsap.killTweensOf(ball.position);
  gsap.killTweensOf(ballPointLight.position);

  const targetAngle = winningNumberIndex * slotAngle;
  const ballSpinDuration = 6;
  const bounceDuration = 0.5;
  const settleDuration = 3.0;

  let ballRadius = wheelRadius + 0.08;
  const N_spins = 4;
  const total_angle = N_spins * 2 * Math.PI + targetAngle;
  let lastSeparatorIndex = -1;

  let ballProgress = 0;
  gsap.to(
    { progress: 0 },
    {
      progress: 1,
      duration: ballSpinDuration,
      ease: "power3.out",
      onUpdate: function () {
        ballProgress = this.progress();
        const t = ballProgress;
        const k = 3;
        let alpha = total_angle * (1 - Math.pow(1 - t, k));
        const ease = 1 - (1 - t) * (1 - t);
        ballRadius = wheelRadius + 0.08 - ease * 0.13;

        const currentAngle = alpha % (2 * Math.PI);
        const separatorIndex = Math.floor(currentAngle / slotAngle);
        let bounceZ = 0;

        if (separatorIndex !== lastSeparatorIndex && t < 0.8) {
          lastSeparatorIndex = separatorIndex;
          const bounceMagnitude = 0.04 * (1 - t);
          const bounceDurationLocal = 0.1;
          gsap.to(ball.position, {
            z: 0.04 + bounceMagnitude,
            duration: bounceDurationLocal / 2,
            yoyo: true,
            repeat: 1,
            ease: "power2.out",
            onComplete: () => {
              ball.position.z = 0.04;
            },
          });
          const jitter = (Math.random() - 0.5) * 0.05;
          alpha += jitter;
        }

        ball.position.x = ballRadius * Math.cos(alpha);
        ball.position.y = ballRadius * Math.sin(alpha);
        ball.position.z = 0.04 + bounceZ;
        ballPointLight.position.set(
          ball.position.x,
          ball.position.y,
          wheelHeight / 2 + 0.07
        );
        ball.visible = true;
      },
      onComplete: () => {
        gsap.to(wheelRotationTween, {
          timeScale: 1,
          duration: 2,
          ease: "power2.inOut",
        });

        gsap.to(ball.position, {
          z: 0.04,
          duration: bounceDuration / 2,
          repeat: 1,
          yoyo: true,
          ease: "power2.out",
          onUpdate: function () {
            const t = this.progress();
            const jitter = Math.sin(t * Math.PI * 0.01) * 0.0;
            ball.position.x =
              (wheelRadius - 0.05) * Math.cos(targetAngle + jitter);
            ball.position.y =
              (wheelRadius - 0.05) * Math.sin(targetAngle + jitter);
            ballPointLight.position.set(
              ball.position.x,
              ball.position.y,
              wheelHeight / 2 + 0.07
            );
          },
          onComplete: () => {
            gsap.to(ball.position, {
              x: (wheelRadius - 0.05) * Math.cos(targetAngle),
              y: (wheelRadius - 0.05) * Math.sin(targetAngle),
              z: 0.04,
              duration: settleDuration,
              ease: "power2.out",
              onUpdate: function () {
                ballPointLight.position.set(
                  ball.position.x,
                  ball.position.y,
                  wheelHeight / 2 + 0.07
                );
              },
              onComplete: () => {
                ball.position.set(
                  (wheelRadius - 0.05) * Math.cos(targetAngle),
                  (wheelRadius - 0.05) * Math.sin(targetAngle),
                  0.04
                );
                ballPointLight.position.set(
                  ball.position.x,
                  ball.position.y,
                  wheelHeight / 2 + 0.07
                );
                isBallSpinning = false;
                callback(winningNumber);

                // Highlight the winning betting area
                highlightWinningBettingArea(winningNumber);

                ball.visible = false;
              },
            });
          },
        });
      },
    }
  );
}

function highlightWinningBettingArea(winningNumber) {
  const winningNumberStr = winningNumber.toString();

  const winningArea = straightBettingAreas.find(
    (area) =>
      area.userData.number.toString() === winningNumberStr ||
      (winningNumberStr === "00" && area.userData.name === "00")
  );

  if (winningArea) {
    // Highlight the betting area
    highlightBettingArea(winningArea, true);

    // Animate highlight effect
    gsap.to(winningArea.userData.overlay.material, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.in",
      yoyo: true,
      repeat: 3,
      onComplete: () => {
        highlightBettingArea(winningArea, false);
      },
    });

    // Get the world position of the winning area
    const worldPosition = new THREE.Vector3();
    winningArea.getWorldPosition(worldPosition);
    worldPosition.y += 0.01; // Offset above the betting area in world space

    // Convert to local position in gltfScene's space
    const localPosition = gltfScene.worldToLocal(worldPosition.clone());

    // Store original turret position and rotation
    const originalPosition = PinModel.position.clone();
    const originalRotation = PinModel.rotation.clone();

    // Define movement parameters
    const liftHeight = 0.1;
    const tiltAngle = Math.PI / 12;
    const moveDuration = 0.9;
    const movementDelay = 1;

    // Move to winning position with animation
    gsap.to(PinModel.position, {
      x: localPosition.x,
      y: localPosition.y + liftHeight,
      z: localPosition.z,
      duration: moveDuration,
      ease: "power2.out",
      delay: movementDelay,
      onUpdate: function () {
        const progress = this.progress();
        const tilt = tiltAngle * Math.sin(progress * Math.PI);
        PinModel.rotation.z = originalRotation.z + tilt;
      },
      onComplete: () => {
        gsap.to(PinModel.position, {
          y: localPosition.y,
          duration: 0.3,
          ease: "power2.in",
          onComplete: () => {
            PinModel.rotation.z = originalRotation.z;
            gsap.delayedCall(1.5, () => {
              gsap.to(PinModel.position, {
                x: originalPosition.x,
                y: originalPosition.y + liftHeight,
                z: originalPosition.z,
                duration: moveDuration,
                ease: "power2.out",
                onUpdate: function () {
                  const progress = this.progress();
                  const tilt = tiltAngle * Math.sin(progress * Math.PI);
                  PinModel.rotation.z = originalRotation.z + tilt;
                },
                onComplete: () => {
                  gsap.to(PinModel.position, {
                    y: originalPosition.y,
                    duration: 0.3,
                    ease: "power2.in",
                    onComplete: () => {
                      PinModel.rotation.z = originalRotation.z;
                    },
                  });
                },
              });
            });
          },
        });
      },
    });
  } else {
    console.warn(`No betting area found for winning number: ${winningNumber}`);
  }
}

/************************ Chip Placement  ************************/
function placeChip(bettingArea, value) {
  if (!coinModel) {
    return;
  }

  const chipInstance = coinModel.clone();
  chipInstance.traverse((child) => {
    if (child.isMesh) {
      const texture = coinTextures[value] || coinTextures[5];
      child.material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: true,
      });
    }
  });

  const currentChips = bettingArea.userData.chips || [];
  const stackHeight = currentChips.length * 0.015;

  chipInstance.position.copy(bettingArea.position);
  chipInstance.position.y = 0.484 + stackHeight;
  chipInstance.scale.set(1, 1, 1);
  chipInstance.rotation.x = -Math.PI * 2;

  // Dynamically calculate width and height based on value
  const baseWidth = 0.1; // Minimum width for smallest value
  const baseHeight = 0.05; // Minimum height
  const scaleFactor = 0.002; // Small scaling factor per unit of value
  const width = baseWidth + value * scaleFactor; // e.g., value=5 -> 0.11, value=30 -> 0.16
  const height = baseHeight + value * scaleFactor * 0.5; // Height scales slower

  // Add text label for the chip value
  const textBlock = new ThreeMeshUI.Block({
    width: width,
    height: height,
    justifyContent: "center",
    alignItems: "center",
    backgroundOpacity: 0,
    fontFamily:
      "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.json",
    fontTexture:
      "https://unpkg.com/three-mesh-ui/examples/assets/Roboto-msdf.png",
  });

  const text = new ThreeMeshUI.Text({
    content: `${value}`, // Display the numeric value
    fontSize: 0.04,
    fontColor: new THREE.Color(0x000000),
  });

  textBlock.add(text);

  // Remove background mesh to avoid default background
  textBlock.background = null;
  // Alternatively, manually remove background mesh from children
  textBlock.children.forEach((child) => {
    if (child.isMesh && child !== text) {
      textBlock.remove(child);
    }
  });

  textBlock.position.copy(chipInstance.position);
  textBlock.position.y += 0.005;
  textBlock.rotation.x = -Math.PI * 0.5;
  textBlock.rotation.z = Math.PI * 0.5;

  const chipGroup = new THREE.Group();
  chipGroup.add(chipInstance);
  chipGroup.add(textBlock);

  scene.add(chipGroup);

  bettingArea.userData.chips.push(chipGroup);
  bettingArea.userData.bets.push(value);

  if (bettingArea.userData.overlay) {
    bettingArea.userData.overlay.material.opacity = 0;
  }
}

function getChipColor(value) {
  switch (value) {
    case 1:
      return 0xff0000; // Red
    case 5:
      return 0x00ff00; // Green
    case 10:
      return 0x0000ff; // Blue
    case 25:
      return 0xffff00; // Yellow
    default:
      return 0xffffff;
  }
}

/************************ Sizes*************************/
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;
  configureCamera(camera, sizes, controls);
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

function configureCamera(camera, sizes, controls) {
  const isLargeScreen = window.innerWidth >= 991;
  const ismobileScreen = window.innerWidth <= 767;

  if (isLargeScreen) {
    camera.fov = 75;
    camera.position.set(3.9, 4.8, -4.2);
    camera.scale.set(1, 1, 3.5);
  } else if (ismobileScreen) {
    camera.fov = 10;
    camera.position.set(0.58, 20, -8);
    camera.scale.set(1, 1, 1);
    scene.position.set(-0.8, -5.52, 2.24);
    scene.rotation.set(0, -0.1, 0);
  } else {
    camera.fov = 80;
    camera.position.set(0.005, 12, -2.7);
    camera.scale.set(1, 1, 5);
  }

  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  if (controls) {
    controls.update();
  }
}

/*************** Camera*********************/

// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
configureCamera(camera, sizes);
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = false;
controls.enableRotate = false; // Disable rotating the camera with the mouse
controls.enableZoom = false; // Disable zooming with the mouse wheel
controls.enablePan = false;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
});
// renderer.toneMapping = THREE.ReinhardToneMapping;
// renderer.toneMappingExposure = 3;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

const debugObject1 = {};
const isLargeScreen = window.innerWidth >= 991;
const ismobileScreen = window.innerWidth <= 767;

debugObject1.landscape_camera = () => {
  gsap.to(camera.position, {
    x: 7,
    y: 6.5,
    z: 0,
    duration: 2,
    ease: "power2.out",
    onUpdate: () => {
      camera.lookAt(new THREE.Vector3(0, 0, 0));
      controls.update();
    },
  });
  gsap.to(controls.target, {
    x: 0.6,
    y: 1,
    z: 0,
    duration: 2,
    ease: "power2.out",
    onUpdate: () => controls.update(),
  });
  gsap.to(camera.scale, {
    x: 1,
    y: 1,
    z: 5,
    duration: 2,
    ease: "power2.out",
    onUpdate: () => {
      camera.lookAt(new THREE.Vector3(0, 0, 0));
      controls.update();
    },
  });
};

debugObject1.bet_placed_camera = () => {
  if (isLargeScreen) {
    gsap.to(camera.position, {
      x: 2.8,
      y: 5.2,
      z: -1.8,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
    gsap.to(controls.target, {
      x: -1.1,
      y: 1,
      z: 0,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => controls.update(),
    });
    gsap.to(camera.scale, {
      x: 1,
      y: 1,
      z: 8.5,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
  } else if (ismobileScreen) {
    gsap.to(camera.position, {
      x: 0.49,
      y: 20,
      z: -8,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
    gsap.to(controls.target, {
      x: -1.51,
      y: 0,
      z: 0.3,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => controls.update(),
    });
    gsap.to(camera.scale, {
      x: 1,
      y: 1,
      z: 2,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
  } else {
    gsap.to(camera.position, {
      x: 0,
      y: 5.4,
      z: -1.5,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
    gsap.to(controls.target, {
      x: -1.3,
      y: 1.1,
      z: 0.15,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => controls.update(),
    });
    gsap.to(camera.scale, {
      x: 1,
      y: 1,
      z: 7,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
  }
};

debugObject1.front_view_camera = () => {
  if (isLargeScreen) {
    gsap.to(camera.position, {
      x: -0.08,
      y: 4.34,
      z: -5.24,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });

    gsap.to(controls.target, {
      x: 0,
      y: 0,
      z: 0,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => controls.update(),
    });
    gsap.to(camera.scale, {
      x: 1,
      y: 1,
      z: 3.5,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
  }
};

debugObject1.default_camera = () => {
  if (isLargeScreen) {
    gsap.to(camera.position, {
      x: 3.7,
      y: 4.8,
      z: -4.2,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });

    gsap.to(controls.target, {
      x: 0,
      y: 0,
      z: 0,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => controls.update(),
    });
    gsap.to(camera.scale, {
      x: 1,
      y: 1,
      z: 3.5,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
  } else if (ismobileScreen) {
    gsap.to(camera.position, {
      x: 0.58,
      y: 20,
      z: -8,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });

    gsap.to(controls.target, {
      x: 0,
      y: 0,
      z: 0,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => controls.update(),
    });
    gsap.to(camera.scale, {
      x: 1,
      y: 1,
      z: 1,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
  } else {
    gsap.to(camera.position, {
      x: 0.005,
      y: 12,
      z: -2.7,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });

    gsap.to(controls.target, {
      x: 0,
      y: 0,
      z: 0,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => controls.update(),
    });
    gsap.to(camera.scale, {
      x: 1,
      y: 1,
      z: 5,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        controls.update();
      },
    });
  }
};
// const cameraFolder = gui.addFolder('Camera');
// cameraFolder.add(camera.position, 'x', -10, 10).name('Position X').listen();
// cameraFolder.add(camera.position, 'y', -10, 10).name('Position Y').listen();
// cameraFolder.add(camera.position, 'z', -10, 10).name('Position Z').listen();

// gui.add(debugObject1, 'landscape_camera');
// gui.add(debugObject1, 'betPlacedBtn');
// gui.add(debugObject1, 'default_camera')

// Add event listeners to buttons
document
  .getElementById("landscapeBtn")
  .addEventListener("click", debugObject1.landscape_camera);
// document.getElementById('betPlacedBtn').addEventListener('click', debugObject1.bet_placed_camera);
document
  .getElementById("betPlacedBtn")
  .addEventListener("click", debugObject1.front_view_camera);
document
  .getElementById("defaultBtn")
  .addEventListener("click", debugObject1.default_camera);

/**
 * Animate
 */
const tick = () => {
  // Update controls
  controls.update();
  // Update points only when the scene is ready
  if (sceneReady) {
    // Go through each point
    for (const point of points) {
      // Get 2D screen position
      const screenPosition = point.position.clone();
      screenPosition.project(camera);

      // Set the raycaster
      raycaster.setFromCamera(screenPosition, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      // No intersect found
      if (intersects.length === 0) {
        // Show
        point.element.classList.add("visible");
      }

      // Intersect found
      else {
        // Get the distance of the intersection and the distance of the point
        const intersectionDistance = intersects[0].distance;
        const pointDistance = point.position.distanceTo(camera.position);

        // Intersection is close than the point
        if (intersectionDistance < pointDistance) {
          // Hide
          point.element.classList.remove("visible");
        }
        // Intersection is further than the point
        else {
          // Show
          point.element.classList.add("visible");
        }
      }

      const translateX = screenPosition.x * sizes.width * 0.5;
      const translateY = -screenPosition.y * sizes.height * 0.5;
      point.element.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`;
    }
  }
  // Render
  renderer.render(scene, camera);
  // Update three-mesh-ui
  ThreeMeshUI.update();
  // Call tick again on the next frame

  window.requestAnimationFrame(tick);
};

tick();
